import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconCircleCheck } from "@tabler/icons-react";
import { ChangeRequestType } from "../../../../../../api";
import { AppRoutes } from "../../../../../../common/routes/app_routes";
import { Button } from "../../../../../../components/common/button";
import { Input } from "../../../../../../components/common/input";
import { Heading, Text } from "../../../../../../components/common/typography";
import { useSubmitChangeRequest } from "../../../../../settings/hooks/useSubmitChangeRequest";
import { useLogout } from "../../../../hooks/useLogout";
import { useRegisterWizard } from "../../../../domain/register-wizard";
import AuthSplitLayout from "../../../../routes/auth-split-layout";
import RegisterWizardLayout from "./register-wizard-layout";
import { DevAutofillButton } from "./dev-autofill-button";
import {
  MAX_LENGTH,
  normalizeIban,
  validateAccountHolder,
  validateBankName,
  validateIban,
} from "../../../../../../utils/validation";

type FieldErrors = Partial<Record<"bankName" | "accountHolder" | "iban", string>>;

export function BankingDetailsStep() {
  const navigate = useNavigate();
  const logout = useLogout();
  const wizard = useRegisterWizard();
  const { submit, isLoading } = useSubmitChangeRequest();

  const [bankName, setBankName] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [iban, setIban] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  const canSubmit = bankName.trim() && accountHolder.trim() && iban.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || isLoading) return;

    const nextErrors: FieldErrors = {
      bankName: validateBankName(bankName) ?? undefined,
      accountHolder: validateAccountHolder(accountHolder) ?? undefined,
      iban: validateIban(iban) ?? undefined,
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    try {
      await submit({
        type: ChangeRequestType.BankAccountCreate,
        proposedChanges: {
          bankName: bankName.trim(),
          accountHolder: accountHolder.trim(),
          iban: normalizeIban(iban),
          isDefault: true,
        },
      });
      setSubmitted(true);
    } catch {
      // toast already shown by useSubmitChangeRequest
    }
  };

  if (submitted) {
    return (
      <AuthSplitLayout>
        <div className="mt-[25vh] flex w-full flex-col items-center gap-6 text-center">
          <span className="inline-flex size-16 items-center justify-center text-accentGreen">
            <IconCircleCheck size={64} />
          </span>
          <Heading variant="h2" tone="dark">
            Banking details submitted
          </Heading>
          <Text variant="body" tone="body">
            Your banking details have been submitted for review. You're all set
            — our team will verify everything within 1–2 business days.
          </Text>
        </div>
        <Button
          variant="primary"
          fullWidth
          onClick={() => {
            wizard.reset();
            navigate(AppRoutes.DASHBOARD);
          }}
        >
          Go to dashboard
        </Button>
        <Button
          variant="secondaryLanding"
          fullWidth
          onClick={() => {
            wizard.reset();
            logout();
          }}
        >
          Log out
        </Button>
      </AuthSplitLayout>
    );
  }

  return (
    <RegisterWizardLayout
      title="Banking details"
      subtitle="Add your bank account so we can process payments. You can also do this later from Settings."
      progress={{ current: 4, total: 4 }}
      onBack={() => navigate(AppRoutes.CREATE_ACCOUNT_DOCUMENTS)}
    >
      <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6">
        <Input
          label="Bank name"
          type="text"
          placeholder="Enter bank name"
          value={bankName}
          maxLength={MAX_LENGTH.bankName}
          error={errors.bankName}
          onChange={(e) => {
            setBankName(e.target.value);
            setErrors((current) => ({ ...current, bankName: undefined }));
          }}
          required
        />
        <Input
          label="Account holder"
          type="text"
          placeholder="Enter account holder name"
          value={accountHolder}
          maxLength={MAX_LENGTH.accountHolder}
          error={errors.accountHolder}
          onChange={(e) => {
            setAccountHolder(e.target.value);
            setErrors((current) => ({ ...current, accountHolder: undefined }));
          }}
          required
        />
        <Input
          label="IBAN"
          type="text"
          placeholder="Enter IBAN"
          value={iban}
          maxLength={MAX_LENGTH.ibanInput}
          error={errors.iban}
          onChange={(e) => {
            setIban(e.target.value);
            setErrors((current) => ({ ...current, iban: undefined }));
          }}
          required
        />

        <Button
          variant="primary"
          type="submit"
          fullWidth
          disabled={!canSubmit || isLoading}
        >
          {isLoading ? "Submitting..." : "Submit for review"}
        </Button>
        <Button
          variant="ghost"
          type="button"
          fullWidth
          disabled={isLoading}
          onClick={() => {
            wizard.reset();
            navigate(AppRoutes.DASHBOARD);
          }}
        >
          Skip for now
        </Button>
        <DevAutofillButton
          onClick={() => {
            setBankName("Al Rajhi Bank");
            setAccountHolder("Dev Company LLC");
            setIban("SA0380000000608010167519");
          }}
        />
      </form>
    </RegisterWizardLayout>
  );
}

export default BankingDetailsStep;
